import type { Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

/**
 * Contract every application module fulfils.
 *
 * Adding a screen means creating its folder and exporting one of these; the
 * central router knows no feature by name.
 */
export interface AppModule {
  /** Module identifier; it shows up in logs and in the development tools. */
  readonly name: string;
  /** Top-level routes (login, 404…). */
  readonly routes: RouteRecordRaw[];
  /** Routes rendered **inside** the application shell. */
  readonly layoutRoutes?: RouteRecordRaw[];
  /**
   * Components the shell renders on every screen, above the current route: things that outlive
   * a navigation, such as work still running in the background. Rendered only inside the shell,
   * so they never reach a public screen.
   */
  readonly overlays?: Component[];
}

/**
 * Gathers the routes of every module and hangs the layout ones off the route
 * that declares itself the host (`meta.layoutHost`).
 *
 * The composer does not import the shell: it only knows the flag. That way the
 * platform module can swap shells without this one finding out.
 */
export function composeRoutes(modules: AppModule[]): RouteRecordRaw[] {
  const topLevel = modules.flatMap((module) => module.routes);
  const layoutChildren = modules.flatMap((module) => module.layoutRoutes ?? []);

  if (layoutChildren.length > 0) {
    const host = topLevel.find((route) => route.meta?.layoutHost === true);

    if (!host) {
      throw new Error(
        'composeRoutes: there are layout routes but no module declares meta.layoutHost',
      );
    }

    host.children = [...(host.children ?? []), ...layoutChildren];
  }

  return topLevel;
}

/**
 * Every module's overlays, in module order.
 *
 * The shell renders what this returns without knowing whose they are, the same way the router
 * hangs layout routes without the shell knowing which screens exist.
 */
export function composeOverlays(modules: AppModule[]): Component[] {
  return modules.flatMap((module) => module.overlays ?? []);
}
